import { __ } from '@wordpress/i18n';
import { addFilter } from '@wordpress/hooks';
import { createHigherOrderComponent } from '@wordpress/compose';
import { InspectorAdvancedControls } from '@wordpress/block-editor';
import { SelectControl, ToggleControl } from '@wordpress/components';
import { useSelect, select } from '@wordpress/data';
import { useEntityRecords } from '@wordpress/core-data';
import { Fragment } from '@wordpress/element';
import api from '@wordpress/api';
import clsx from 'clsx';

const allowedBlocks = [ 'core/image', 'core/button' ];
const popperBlocks = [ 'popper/popup', 'popper/button', 'popper/body' ];

const addRule = ( val ) => {
	if ( ! val ) {
		return;
	}

	const postId = select( 'core/editor' ).getCurrentPostId();
	const postType = select( 'core/editor' ).getCurrentPostType();

	const rule = {
		rule: 'post:' + postType,
		object: [ postId ],
	};

	const popups = select( 'core' ).getEntityRecords( 'postType', 'popper', {
		per_page: -1,
	} );
	const popup = popups.find( ( x ) => x.id === Number( val ) );

	const ruleExists = popup.meta.popper_rules.location.find(
		( x ) => x.rule === rule.rule && x.object.includes( postId )
	);

	if ( ruleExists ) {
		return;
	}

	popup.meta.popper_rules.location.push( rule );
	const post = new api.models.Popper( { id: val } );
	post.fetch().done( () => {
		post.setMeta( 'popper_rules', popup.meta.popper_rules );
		post.save( { id: val } );
	} );
};

const withAdvancedControls = createHigherOrderComponent( ( BlockEdit ) => {
	return ( props ) => {
		if ( ! allowedBlocks.includes( props.name ) ) {
			return <BlockEdit { ...props } />;
		}

		const opts = [
			{ value: undefined, label: __( 'Select a popup', 'popper' ) },
		];

		const { records: popups } = useEntityRecords( 'postType', 'popper', {
			per_page: -1,
		} );

		popups?.forEach( ( post ) => {
			const label = post.title.raw || __( '(No title)', 'popper' );
			opts.push( {
				value: post.id,
				label,
			} );
		} );

		const { attributes, setAttributes, isSelected } = props;

		const { popper } = attributes;

		return (
			<Fragment>
				<BlockEdit { ...props } />
				{ isSelected && (
					<InspectorAdvancedControls>
						<SelectControl
							label={ __( 'Open popup' ) }
							value={ popper }
							options={ opts }
							onChange={ ( val ) => {
								setAttributes( { popper: val } );
								addRule( val );
							} }
							help={ __(
								'Open a popup on anchor click',
								'popper'
							) }
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
					</InspectorAdvancedControls>
				) }
			</Fragment>
		);
	};
}, 'withAdvancedControls' );

const withResponsiveControls = createHigherOrderComponent( ( BlockEdit ) => {
	return ( props ) => {
		const { postType } = useSelect( ( select ) => ( {
			postType: select( 'core/editor' ).getCurrentPostType(),
		} ) );

		if ( 'popper' !== postType || popperBlocks.includes( props.name ) ) {
			return <BlockEdit { ...props } />;
		}

		const { attributes, setAttributes, isSelected } = props;

		const { hideDesktop, hideMobile, hideTablet } = attributes;

		const customClass = clsx( {
			'hide-desktop': hideDesktop,
			'hide-tablet': hideTablet,
			'hide-mobile': hideMobile,
		} );

		return (
			<Fragment>
				<BlockEdit { ...props } className={ customClass } />
				{ isSelected && (
					<InspectorAdvancedControls>
						<ToggleControl
							label={ __( 'Hide on desktop' ) }
							checked={ hideDesktop }
							onChange={ ( val ) => {
								setAttributes( { hideDesktop: val } );
							} }
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Hide on tablet' ) }
							checked={ hideTablet }
							onChange={ ( val ) => {
								setAttributes( { hideTablet: val } );
							} }
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Hide on mobile' ) }
							checked={ hideMobile }
							onChange={ ( val ) => {
								setAttributes( { hideMobile: val } );
							} }
							__nextHasNoMarginBottom
						/>
					</InspectorAdvancedControls>
				) }
			</Fragment>
		);
	};
}, 'withResponsiveControls' );

addFilter(
	'editor.BlockEdit',
	'popper/popup-advanced-control',
	withAdvancedControls
);

addFilter(
	'editor.BlockEdit',
	'popper/popup-advanced-control',
	withResponsiveControls
);

/**
 * Add custom attribute for mobile visibility.
 *
 * @param {Object} settings Settings for the block.
 *
 * @return {Object} settings Modified settings.
 */
function addAttributes( settings ) {
	//check if object exists for old Gutenberg version compatibility
	if ( allowedBlocks.includes( settings.name ) ) {
		settings.attributes = Object.assign( settings.attributes, {
			popper: {
				type: 'numeric',
				default: null,
			},
		} );
	}

	if ( ! popperBlocks.includes( settings.name ) ) {
		settings.attributes = Object.assign( settings.attributes, {
			hideDesktop: {
				type: 'boolean',
			},
			hideTablet: {
				type: 'boolean',
			},
			hideMobile: {
				type: 'boolean',
			},
		} );
	}

	return settings;
}

addFilter(
	'blocks.registerBlockType',
	'popper/popup-custom-attributes',
	addAttributes
);

function applyExtraClass( extraProps, blockType, attributes ) {
	const { hideDesktop, hideMobile, hideTablet } = attributes;

	if ( popperBlocks.includes( blockType.name ) ) {
		return extraProps;
	}

	if ( typeof hideDesktop !== 'undefined' && hideDesktop ) {
		extraProps.className = clsx( extraProps.className, 'hide-desktop' );
	}
	if ( typeof hideTablet !== 'undefined' && hideTablet ) {
		extraProps.className = clsx( extraProps.className, 'hide-tablet' );
	}
	if ( typeof hideMobile !== 'undefined' && hideMobile ) {
		extraProps.className = clsx( extraProps.className, 'hide-mobile' );
	}

	return extraProps;
}

addFilter(
	'blocks.getSaveContent.extraProps',
	'popper-pro/applyExtraClass',
	applyExtraClass
);

addFilter( 'editor.preSavePost', 'editor', ( edits ) => {
	const parser = new DOMParser();

	const htmlDoc = parser.parseFromString( edits.content, 'text/html' );

	const links = htmlDoc.querySelectorAll( 'a[data-type=popper]' );
	for ( let i = 0, max = links.length; i < max; i++ ) {
		const { id } = links[ i ].dataset;
		addRule( id );
	}

	return Promise.resolve( edits );
} );
